import { useState } from "react";
import useReveal from "../../hooks/useReveal";
import { MENU, MENU_TABS } from "../../lib/data";

const PriceTag = ({ price }) => (
  <div className="flex items-baseline gap-1 whitespace-nowrap pl-4">
    <span className="font-serif text-xl md:text-2xl text-zp-navy font-medium">
      {price}
    </span>
    <span className="font-serif text-sm md:text-base text-zp-muted">€</span>
  </div>
);

const MenuItem = ({ item, testId }) => (
  <div
    data-testid={testId}
    className="py-4 md:py-5 border-b border-zp-line/70"
  >
    <div className="flex items-baseline justify-between gap-3">
      <h4 className="font-serif text-lg md:text-xl text-zp-navy leading-snug">
        {item.name}
      </h4>
      <PriceTag price={item.price} />
    </div>
    {item.desc && (
      <p className="mt-1 text-sm text-zp-muted font-light leading-relaxed pr-8">
        {item.desc}
      </p>
    )}
  </div>
);

const FlatList = ({ items, tab }) => (
  <div className="grid md:grid-cols-2 gap-x-16 gap-y-0">
    {items.map((item, i) => (
      <MenuItem key={i} item={item} testId={`menu-item-${tab}-${i}`} />
    ))}
  </div>
);

const GroupedList = ({ groups, tab }) => (
  <div className="space-y-14 md:space-y-20">
    {groups.map((group, gi) => (
      <div key={gi} data-testid={`menu-group-${tab}-${gi}`}>
        <div className="flex items-baseline gap-4 mb-6 md:mb-8">
          <span className="dot" />
          <h3 className="font-serif text-2xl md:text-3xl text-zp-navy tracking-tight">
            {group.title}
          </h3>
          {group.note && (
            <span className="overline text-[10px] text-zp-muted">
              {group.note}
            </span>
          )}
        </div>
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-0">
          {group.items.map((item, i) => (
            <MenuItem
              key={i}
              item={item}
              testId={`menu-item-${tab}-${gi}-${i}`}
            />
          ))}
        </div>
        {group.footnote && (
          <p className="mt-5 text-xs italic font-serif text-zp-muted">
            {group.footnote}
          </p>
        )}
      </div>
    ))}
  </div>
);

export const Menu = () => {
  const [active, setActive] = useState("tapas");
  const headRef = useReveal();

  const section = MENU[active] || {};
  const isGrouped = Array.isArray(section.groups);

  return (
    <section
      id="carte"
      data-testid="menu-section"
      className="bg-zp-cream py-24 md:py-40 relative"
    >
      <div className="absolute top-0 left-0 right-0 h-1 cabana-stripes-thin opacity-40" />

      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div
          ref={headRef}
          className="reveal grid lg:grid-cols-12 gap-8 items-end mb-12 md:mb-16"
        >
          <div className="lg:col-span-7">
            <p className="overline text-zp-navy/60 mb-6 flex items-center gap-3">
              <span className="dot" /> La carte
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-zp-navy">
              Tapas, douceurs,
              <br />
              <span className="italic font-light">cocktails & bons vins.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-zp-muted text-base md:text-lg leading-relaxed font-light">
              Une carte généreuse à partager — entrées, tapas, planches,
              salades, plats du jour, douceurs maison et un bar complet
              signé Zénith. Tous nos plats et desserts sont faits maison.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div
          data-testid="menu-tabs"
          className="flex flex-wrap gap-1 md:gap-3 border-b border-zp-line pb-1 mb-12 md:mb-16"
        >
          {MENU_TABS.map((tab) => (
            <button
              key={tab.id}
              data-testid={`menu-tab-${tab.id}`}
              onClick={() => setActive(tab.id)}
              className={`px-5 py-3 text-sm tracking-widest uppercase font-medium transition-all duration-300 relative ${
                active === tab.id
                  ? "text-zp-navy"
                  : "text-zp-muted hover:text-zp-navy"
              }`}
            >
              {tab.label}
              {active === tab.id && (
                <span className="absolute left-1/2 -bottom-[5px] -translate-x-1/2 w-8 h-[2px] bg-zp-lemon" />
              )}
            </button>
          ))}
        </div>

        <div key={active}>
          {isGrouped ? (
            <GroupedList groups={section.groups} tab={active} />
          ) : (
            <FlatList items={section.items || []} tab={active} />
          )}
        </div>

        <p className="mt-14 text-center text-sm text-zp-muted italic font-serif">
          Tous nos plats et desserts sont faits maison. Prix TTC, services
          compris. Un document détaillant les allergènes est disponible sur
          demande. Pour les groupes (8+), réservation au{" "}
          <a href="tel:+33609976365" className="underline text-zp-navy">
            06 09 97 63 65
          </a>
          .
        </p>
      </div>
    </section>
  );
};

export default Menu;
